import * as api from "../../lib/api.js";
import * as store from "../../lib/store.js";
import { flush as flushTelemetry, log as logTelemetry } from "../../lib/telemetry.js";
import { loadNormalisedCredentials } from "./credentials";
import { forgetServerSubscription } from "./push";
import { endSignIn } from "./session-ended";
import { rotationSettled } from "./token-rotation";
import { t } from "../contract/strings";
import { captureAnalytics } from "../analytics";
import { clearReturnTo } from "../state/return-to";
import { useApp, type Account } from "../state/store";

/**
 * Signing in, and why every part of it is where it is.
 *
 * **The token travels in the URL fragment, never the query string**, and that choice is
 * load-bearing in both directions. A magic-link token in a query string is fetched by mail
 * scanners and link prefetchers -- which would consume a single-use credential before its
 * owner ever tapped it -- and it lands in server logs and `Referer` headers. A fragment is
 * never sent to any server at all, so the only thing that can spend the token is the
 * function below. It is also why this app's routes are paths: hash routing and `#signin=`
 * would be two things reading one slot.
 *
 * **Signing in is optional and stays optional.** Recording, joining and reading a
 * transcript all run on the device token; an account is what makes history and a lost
 * device survivable. The account credential is an HttpOnly cookie the page cannot read.
 */

/** What a fragment turns out to have been. `""` is "nothing to say" -- see `cancelled`. */
export type SignInOutcome = {
  kind: "ok" | "warn" | "err" | "";
  message: string;
};

/** The ways in, as the shared analytics enum names them (#1085). */
export type SignInMethod = "magic_link" | "google" | "apple" | "code";

/**
 * The provider last chosen, kept across the redirect that leaves the app.
 *
 * The return fragment says only that a provider signed in, not which one. Session
 * storage is the tab's own and dies with it, so the method never outlives the flow it
 * belongs to. A browser that blocks storage simply reports the return as Google's, the
 * older provider, and never sends an address or a token.
 */
const SIGN_IN_METHOD_KEY = "meetfolks-signin-method";

function rememberSignInMethod(method: SignInMethod): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_METHOD_KEY, method);
  } catch {
    /* the flow still completes; only the return's method is less precise */
  }
}

function takeSignInMethod(fallback: SignInMethod): SignInMethod {
  try {
    const saved = window.sessionStorage.getItem(SIGN_IN_METHOD_KEY);
    window.sessionStorage.removeItem(SIGN_IN_METHOD_KEY);
    if (saved === "magic_link" || saved === "google" || saved === "apple" || saved === "code") {
      return saved;
    }
  } catch {
    /* fall through */
  }
  return fallback;
}

//: Boot must decide whether to block recording, so its two reads are bounded (#38).
//: Generous enough not to fire on a slow device, short enough that a captive portal is
//: an explicit "could not check" rather than a spinner that never resolves.
const ACCOUNT_FETCH_TIMEOUT_MS = 10_000;
const LINK_FETCH_TIMEOUT_MS = 10_000;

/** The native apps' sign-in return (`app_sign_in` in `contract/routes.json`, #796). */
export const NATIVE_RETURN_PATH = "/app/signin";

export type SignInFragment =
  | { kind: "token"; token: string }
  | { kind: "returned" }
  | { kind: "error"; message: string }
  | { kind: "cancelled" }
  | null;

/**
 * The four shapes a sign-in flow leaves behind, parsed and nothing more.
 *
 * Separated from the consuming below so it can be tested without a document: the parsing is
 * the part with four cases, and the part with the ordering constraint is the other one.
 */
export function parseSignInFragment(hash: string): SignInFragment {
  const value = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!value) return null;
  if (value.startsWith("signin=")) {
    // `signin=<token>`. The token is URL-quoted, so it is the whole rest of the fragment.
    return { kind: "token", token: decodeURIComponent(value.slice("signin=".length)) };
  }
  if (value === "signed-in") return { kind: "returned" };
  if (value.startsWith("signin-error=")) {
    return { kind: "error", message: decodeURIComponent(value.slice("signin-error=".length)) };
  }
  if (value === "signin-cancelled") return { kind: "cancelled" };
  return null;
}

/** Everything the consuming touches, injectable so the ordering below can be asserted. */
export interface SignInDeps {
  location: Pick<Location, "hash" | "pathname" | "search">;
  replaceUrl: (url: string) => void;
  verifyMagicLink: (token: string) => Promise<Account>;
  account: () => Promise<Account | null>;
  linkDevice: () => Promise<unknown>;
  hasCredentials: () => boolean;
  setAccount: (account: Account | null) => void;
  resetDevice: () => Promise<void>;
}

function realDeps(): SignInDeps {
  return {
    location: window.location,
    replaceUrl: (url) => window.history.replaceState(null, "", url),
    verifyMagicLink: api.verifyMagicLink,
    account: () => api.account({ timeoutMs: ACCOUNT_FETCH_TIMEOUT_MS }),
    linkDevice: () => api.linkDevice({ timeoutMs: LINK_FETCH_TIMEOUT_MS }),
    hasCredentials: () => Boolean(useApp.getState().credentials),
    setAccount: (account) => useApp.getState().setAccount(account),
    resetDevice: setUpAsNewDeviceForAccount,
  };
}

/**
 * Act on whatever the sign-in flows left in the URL fragment.
 *
 * The fragment is cleared either way, so a reload does not replay a sign-in or re-show an
 * error somebody has already read -- and it is cleared **before the network call**, not
 * after. A single-use token that stays in the URL is replayed by a reload, and the person
 * then sees "already used" for a sign-in that worked perfectly.
 *
 * An empty hash returns before any of that: an ordinary load must not touch history.
 */
export async function consumeSignInFragment(
  deps: SignInDeps = realDeps()
): Promise<SignInOutcome | null> {
  if (deps.location.pathname === NATIVE_RETURN_PATH) {
    // The native apps' return (#796): its token was issued with the app's PKCE challenge,
    // and only the app holds the verifier. Verifying it here would spend a single-use link
    // the browser cannot finish, so it is dropped unread -- with the query's `?code=` -- and
    // the router sends this page to sign-in.
    if (deps.location.hash || deps.location.search) deps.replaceUrl(deps.location.pathname);
    return null;
  }
  const fragment = parseSignInFragment(deps.location.hash);
  if (!fragment) return null;

  deps.replaceUrl(deps.location.pathname + deps.location.search);

  if (fragment.kind === "token") {
    try {
      return await finishSignIn(await deps.verifyMagicLink(fragment.token), deps, "magic_link");
    } catch (err) {
      captureAnalytics("sign_in_failed", { method: "magic_link", category: "request" });
      return { kind: "err", message: (err as Error).message };
    }
  }

  if (fragment.kind === "returned") {
    // Back from Google or Apple. The cookie is already set -- the callback did that -- so
    // this only has to find out who we are now.
    const method = takeSignInMethod("google");
    const account = await deps.account().catch(() => null);
    if (account) return finishSignIn(account, deps, method);
    deps.setAccount(null);
    captureAnalytics("sign_in_failed", { method, category: "provider" });
    return { kind: "err", message: t("signin.outcome_failed") };
  }

  if (fragment.kind === "error") {
    captureAnalytics("sign_in_failed", { method: takeSignInMethod("google"), category: "provider" });
    return { kind: "err", message: fragment.message };
  }

  // Cancelled: they changed their mind, which is not news. The fragment is still cleared.
  return { kind: "", message: "" };
}

/**
 * What every completed sign-in does next, whichever way it came in: record the account,
 * link this device to it, and say so -- or say why the link did not happen.
 */
async function finishSignIn(
  account: Account,
  deps: SignInDeps,
  method: SignInMethod
): Promise<SignInOutcome> {
  deps.setAccount(account);
  const warning = await linkThisDevice(deps);
  captureAnalytics("sign_in_completed", { method });
  if (warning) return { kind: "warn", message: warning };
  return { kind: "ok", message: t("signin.outcome_signed_in", { email: account.email }) };
}

/**
 * Sign the store reviewers' account in with its code (#800).
 *
 * Only offered after the server answered the review address with `code_sign_in`. A 400 is
 * the one refusal for a wrong code; anything else (a 429, a network failure) says what the
 * server or the transport said.
 */
export async function signInWithReviewCode(
  email: string,
  code: string,
  deps: SignInDeps = realDeps(),
  verify: (email: string, code: string) => Promise<Account> = api.signInWithReviewCode
): Promise<SignInOutcome> {
  let account: Account;
  try {
    account = await verify(email, code);
  } catch (err) {
    const failure = err as { status?: number; message?: string };
    captureAnalytics("sign_in_failed", { method: "code", category: "request" });
    if (failure.status === 400) return { kind: "err", message: t("signin.code_wrong") };
    return { kind: "err", message: failure.message ?? t("signin.outcome_failed") };
  }
  return finishSignIn(account, deps, "code");
}

type LinkTrigger = "signin" | "boot" | "enrollment";

/**
 * Attach this device to the account that just signed in (#38).
 *
 * Both credentials in one request -- the cookie says who is asking, the device token says
 * which device is being claimed -- so neither alone can link anything.
 *
 * Failures are returned rather than displayed, because the caller decides what screen is up
 * and writes the final line; displaying it here would put a warning on a screen that is not
 * showing and then have it overwritten by the success message. A 409 -- this device already
 * belongs to somebody else's account -- is not a failure to report: the device is reset for
 * whoever signed in (#336).
 *
 * The outcome is *also* written to `deviceAccountLink`, because a returned string cannot
 * gate anything: the engine backstop and Home both read that state, and it survives the
 * screen that showed the warning.
 */
export async function linkThisDevice(
  deps: SignInDeps = realDeps(),
  trigger: LinkTrigger = "signin"
): Promise<string | null> {
  const app = useApp.getState();
  if (!deps.hasCredentials()) {
    // No bearer token yet, so a link outcome event could not be sent anyway -- the
    // telemetry POST requires the token (§api.js) and drops a 401 as terminal. Enrollment
    // emits the meaningful signal instead, once it has registered.
    app.setDeviceAccountLink({ status: "not_applicable", reason: "no_device" });
    return null;
  }
  if (!app.account) {
    app.setDeviceAccountLink({ status: "not_applicable", reason: "signed_out" });
    logTelemetry("link_skipped", { trigger, reason: "no_account" });
    flushTelemetry();
    return null;
  }

  app.setDeviceAccountLink({ status: "checking" });
  try {
    await deps.linkDevice();
    app.setDeviceAccountLink({ status: "linked", accountId: app.account.account_id ?? "" });
    logTelemetry("link_success", { trigger });
    flushTelemetry();
    return null;
  } catch (err) {
    const failure = err as { status?: number; data?: Record<string, unknown>; message?: string };
    if (failure.status === 409 && failure.data?.code === "device_account_conflict") {
      // Closed enums only: the email is never telemetered.
      logTelemetry("link_conflict", { trigger, status: 409 });
      if (trigger !== "enrollment") {
        // Sent before the reset, which takes the device token the telemetry POST needs:
        // this event is the only record that a silent reset happened.
        try {
          await flushTelemetry();
        } catch {
          /* best effort */
        }
        // Whoever signed in is who this device is for now (#336). Devices never move
        // between accounts, so the old identity is wiped silently -- no prompt, no
        // message -- and the route guard sends the device on to enrolment. Nothing
        // unsent is lost: pending audio and claims live only in a running meeting page,
        // and sign-in and boot both start from a fresh load.
        //
        // A failed local wipe is swallowed too: the reset clears the identity in memory
        // regardless, so the device still goes to enrolment, and a raw storage error is
        // nothing the person can act on.
        await deps.resetDevice().catch(() => {});
        return null;
      }
      flushTelemetry();
      // A device minted by this very enrolment belongs to no account yet, so this cannot
      // happen; if it ever does, fail closed rather than wipe what was just recorded.
      app.setDeviceAccountLink({ status: "conflict" });
      return t("signin.outcome_unlinked");
    }
    app.setDeviceAccountLink({
      status: "error",
      message: failure.message ?? t("signin.outcome_link_unknown"),
    });
    return t("signin.outcome_link_error", { message: (err as Error).message });
  }
}

/**
 * Set this device up again, as a new one, for the account that is signed in (#38, #336).
 *
 * The local half is destroyed on purpose: a new device row, keypair and voice template
 * is what lets the new person own their claims. The account cookie is deliberately *not*
 * cleared, so enrolment links the fresh device to the account already signed in. The old
 * server row stays under its old account, untouched and still verifiable -- stored results
 * reference its participant id.
 *
 * Not `signOutAndClearDevice`: that revokes using the *signed-in* account, and the device
 * being abandoned belongs to a different one, so the revoke would 404 and the sign-out
 * would throw away the account the person wants to keep.
 *
 * Order matters. A token rotation boot started is let land first: wiping before it would
 * let its save put the old device's credentials back into the wiped store. Then the push
 * address is dropped while the old token can still authorise it -- best effort, failing
 * it must not stop the wipe.
 */
export async function setUpAsNewDeviceForAccount(): Promise<void> {
  await rotationSettled();
  await forgetServerSubscription().catch(() => {});
  api.setToken(null);
  try {
    await store.wipeAll();
  } finally {
    const app = useApp.getState();
    app.setIdentity({ credentials: null, template: null, keypair: null });
    app.setDeviceAccountLink({ status: "not_applicable", reason: "no_device" });
  }
}

/** Whoever this browser was, it is not them any more -- whatever the server says. */
export async function signOutOfAccount(): Promise<void> {
  try {
    await api.signOut();
  } catch {
    /* The cookie is cleared server-side or it is not; either way, stop showing this device
       as signed in. */
  }
  clearReturnTo();
  useApp.getState().setAccount(null);
}

/**
 * A full sign-out: revoke this device on the server, kill the browser login,
 * and wipe the *entire* local device store. Nothing is left in IndexedDB, and the
 * server row is marked dead so its token stops working even if a copy of it was
 * taken before sign-out (the gap the light ``signOutOfAccount`` leaves open).
 *
 * The cost is the point: the voice template is gone, so the next session on this device
 * starts with re-enrollment. Past sessions stay on the account -- revoking kills the
 * token, not the evidence (see ``revoke_device``'s docstring).
 *
 * Order matters. ``revokeDevice`` is account-scoped and needs the cookie, so it runs
 * *before* ``signOut`` clears it. The local wipe is best-effort and must happen whether
 * or not the server is reachable: clearing this device is what the caller asked for, and
 * an offline sign-out still has to clear the biometric data.
 */
export async function signOutAndClearDevice(): Promise<void> {
  const creds = await loadNormalisedCredentials();
  if (creds?.claim_profile_id) {
    // Best-effort: a 401/404 here (already signed out, device not linked to this
    // account) must not stop the local wipe. Clearing this device is the point.
    await api.revokeDevice(creds.claim_profile_id).catch(() => {});
  }
  try {
    await api.signOut();
  } catch {
    /* cookie cleared server-side or not; carry on */
  }
  api.setToken(null);
  await store.wipeAll();
  clearReturnTo();
  useApp.getState().setAccount(null);
  useApp.getState().setIdentity({ credentials: null, template: null, keypair: null });
}

/**
 * Delete the signed-in account (#781), then wipe this device as signing out does.
 *
 * The server goes first and must succeed: a refusal (a meeting still going, offline) throws
 * and leaves everything as it was, so the person can try again. Once it has answered, the
 * account, its sign-ins and every device token are already gone server-side, so there is
 * nothing to revoke or sign out of -- only the local voice profile and keys to clear.
 */
export async function deleteAccountAndClearDevice(): Promise<void> {
  await api.deleteAccount();
  api.setToken(null);
  await store.wipeAll();
  clearReturnTo();
  useApp.getState().setAccount(null);
  useApp.getState().setIdentity({ credentials: null, template: null, keypair: null });
}

/**
 * Refresh who is signed in, if anyone.
 *
 * Started inside the model-download window at boot and awaited after it, so it costs
 * nothing: the Create button says one of two different things depending on the answer, and
 * a signed-in device would otherwise be told to sign in for a moment on every single boot.
 */
export async function refreshAccount(
  onConfirmed?: (account: Account) => void
): Promise<Account | null> {
  let account: Account | null = null;
  let reachable = true;
  try {
    account = await api.account({ timeoutMs: ACCOUNT_FETCH_TIMEOUT_MS });
  } catch {
    reachable = false;
  }
  const app = useApp.getState();
  if (reachable && !account && app.account) {
    // Signed in a moment ago and refused now: the sign-in has ended elsewhere (#854).
    await endSignIn();
    return null;
  }
  app.setAccount(account);
  if (!reachable) {
    // Could not ask who is signed in. For an enrolled device that is a blocked state,
    // not a signed-out one: reading "no answer" as "no account" is exactly what let a
    // mismatched device record under whatever cookie was present (#38).
    app.setDeviceAccountLink({
      status: "error",
      message: t("signin.outcome_unreachable"),
    });
    return null;
  }
  // Signed in, confirmed: whoever asked can start work that needs the account beside the
  // device link below rather than after it (boot starts Home's reads here, #1066).
  if (account) onConfirmed?.(account);
  // Reconcile the device link here, not only at sign-in: a reload throws the one-shot
  // sign-in outcome away, and without this the conflict was invisible until the next
  // sign-in, so a reloaded device could record under the mismatched account (#38).
  await linkThisDevice(realDeps(), "boot");
  return account;
}

/**
 * A full navigation, not fetch.
 *
 * The endpoint answers 302 to Google's consent screen and sets an HttpOnly state cookie on
 * the way; both need the browser to be the one following it.
 */
export function signInWithGoogle(): void {
  captureAnalytics("sign_in_started", { method: "google" });
  rememberSignInMethod("google");
  window.location.href = "/v1/accounts/google/start";
}

/**
 * The same, for Sign in with Apple (#802). Apple posts back to the server's callback, which
 * returns here with the same `#signed-in` / `#signin-error` fragments Google's does.
 */
export function signInWithApple(): void {
  captureAnalytics("sign_in_started", { method: "apple" });
  rememberSignInMethod("apple");
  window.location.href = "/v1/accounts/apple/start";
}
